import { Alert, AlertTitle, Typography } from '@mui/material';
import { compact } from 'lodash';
import PropTypes from 'prop-types';

import {
  NON_ROUTABLE_RESOLUTION_TYPE_LABEL,
  NON_ROUTABLE_STATUS_LABEL,
  labelOf,
} from '../constants/nonRoutableRequests';
import { formatParkedAt } from '../utils/nonRoutableRequestDates';

/**
 * Renders the result of an attempted resolution when it was not a clean success.
 *
 * The copy comes from the typed error CODE, never the message — see
 * utils/nonRoutableRequestErrors.js for why each code says what it says.
 */
const NonRoutableResolutionOutcome = ({ outcome }) => {
  const { described, resolvedRequest } = outcome;

  // Only ever populated for NOT_PENDING, where the question is not just WHICH status the request
  // is in but what the person who got there first did. `resolutionType` is null on a cancelled
  // row by database constraint — cancelling is not a way of resolving — so its absence is a fact,
  // not a gap, and nothing is invented to fill it.
  const resolution =
    resolvedRequest &&
    compact([
      `This request is now ${labelOf(NON_ROUTABLE_STATUS_LABEL, resolvedRequest.status)}`,
      resolvedRequest.resolutionType &&
        labelOf(NON_ROUTABLE_RESOLUTION_TYPE_LABEL, resolvedRequest.resolutionType),
      resolvedRequest.resolvedAt && formatParkedAt(resolvedRequest.resolvedAt),
      resolvedRequest.resolvedByPomUserId && `by ${resolvedRequest.resolvedByPomUserId}`,
    ]).join(' · ');

  return (
    <Alert
      severity={described.severity}
      sx={{ mt: 2 }}
      data-testid={`non-routable-outcome-${described.code ?? 'UNKNOWN'}`}
    >
      <AlertTitle>{described.title}</AlertTitle>
      {described.detail}
      {resolution && (
        <Typography variant='body2' sx={{ mt: 1, fontWeight: 600 }}>
          {`${resolution}.`}
        </Typography>
      )}
      {/* The Retry's secondary code (retryRefusal / replayErrorCode) rides beside the primary
          one. Codes only, never a message — it is what the operator quotes to engineering. */}
      {described.code && (
        <Typography variant='caption' display='block' color='textSecondary' sx={{ mt: 1 }}>
          {described.detailCode ? `${described.code} · ${described.detailCode}` : described.code}
        </Typography>
      )}
    </Alert>
  );
};

NonRoutableResolutionOutcome.propTypes = {
  outcome: PropTypes.shape({
    described: PropTypes.shape({
      code: PropTypes.string,
      detailCode: PropTypes.string,
      severity: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      detail: PropTypes.string.isRequired,
    }).isRequired,
    // The by-id read of the row somebody else already resolved. Absent on every other code, and
    // absent too when that read itself failed — the alert then falls back to its own wording.
    resolvedRequest: PropTypes.shape({
      status: PropTypes.string,
      resolutionType: PropTypes.string,
      resolvedAt: PropTypes.string,
      resolvedByPomUserId: PropTypes.string,
    }),
  }).isRequired,
};

export default NonRoutableResolutionOutcome;
